/**
 * AuthorizationQueue — S3.11C.
 *
 * Fila de dispositivos aguardando autorização do NOC (authorization_status
 * 'pending') + visão de bloqueados ('blocked'). Só 'authorized' é operacional;
 * pending/blocked ficam ocultos das listas/mapa/ações até o NOC decidir aqui.
 *
 * Ações críticas (Autorizar / Bloquear) reusam a infra S3.10 (senha do usuário
 * OU ADMIN_TOKEN). O hook useCriticalConfirm fica NESTE escopo (lição S3.10B).
 * O backend exige super_admin/bancada (403 caso contrário).
 */
import { useCallback, useEffect, useState } from "react";

import { authorizeDevice, blockDevice, listDevices } from "../lib/api.js";
import { shortDeviceId } from "../lib/format.js";
import { useCriticalConfirm } from "./ConfirmCriticalAction.jsx";

const POLL_MS = 15000;

function fmtWhen(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return String(ts);
  }
}

export default function AuthorizationQueue() {
  const [filter, setFilter] = useState("pending"); // pending | blocked
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [tick, setTick] = useState(0);
  // S3.10B — hook no escopo de uso (esbuild não pega var fora de escopo).
  const confirmCritical = useCriticalConfirm();

  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    setLoading(true);
    listDevices({ authorization: filter, signal: ac.signal })
      .then((list) => {
        if (alive) setDevices(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setErr("Falha ao carregar a fila de autorização.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    const id = setInterval(reload, POLL_MS);
    return () => {
      alive = false;
      ac.abort();
      clearInterval(id);
    };
  }, [filter, tick, reload]);

  async function onAuthorize(d) {
    setErr(null);
    const res = await confirmCritical({
      title: "Autorizar dispositivo",
      actionLabel: "Autorizar (tornar operacional)",
      target: { deviceId: d.device_id },
      impact:
        "O dispositivo passa a aparecer nas listas/mapa e a receber comando/" +
        "OTA/template/automação/alerta. Vincula o binding pendente e aplica o " +
        "template diferido, se houver.",
      collectReason: true,
      reasonLabel: "Motivo (opcional)",
    });
    if (res == null) return;
    try {
      setBusyId(d.id);
      await authorizeDevice(d.device_id, {
        reason: res.reason,
        confirm: res.secret,
      });
      reload();
    } catch (e) {
      setErr(e?.message || "Falha ao autorizar dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  async function onBlock(d) {
    setErr(null);
    const res = await confirmCritical({
      title: "Bloquear dispositivo",
      actionLabel: "Bloquear (negar)",
      target: { deviceId: d.device_id },
      impact:
        "O dispositivo fica oculto e não recebe nada operacional. Pode ser " +
        "autorizado depois.",
      collectReason: true,
      reasonLabel: "Motivo do bloqueio (opcional)",
    });
    if (res == null) return;
    try {
      setBusyId(d.id);
      await blockDevice(d.device_id, {
        reason: res.reason,
        confirm: res.secret,
      });
      reload();
    } catch (e) {
      setErr(e?.message || "Falha ao bloquear dispositivo.");
    } finally {
      setBusyId(null);
    }
  }

  const blocked = filter === "blocked";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <FilterBtn active={!blocked} onClick={() => setFilter("pending")} tone="amber">
          Aguardando ({!blocked ? devices.length : "…"})
        </FilterBtn>
        <FilterBtn active={blocked} onClick={() => setFilter("blocked")} tone="red">
          Bloqueados ({blocked ? devices.length : "…"})
        </FilterBtn>
        <button
          type="button"
          onClick={reload}
          className="ml-auto text-xs px-2.5 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text"
        >
          ↻ Atualizar
        </button>
      </div>

      {err && (
        <div className="card-pad border border-noc-err/30 bg-noc-err/10 text-sm text-noc-err">
          {err}
        </div>
      )}

      <div className="rounded-lg border border-noc-border bg-noc-warn/10 px-3 py-2 text-xs text-noc-warn">
        {blocked
          ? "Dispositivos bloqueados (negados/suspeitos). Podem voltar a operar via Autorizar."
          : "Dispositivos novos aguardando autorização. Só após Autorizar entram nas listas/mapa e recebem ações. Heartbeat/telemetria seguem normais."}
      </div>

      {loading && devices.length === 0 ? (
        <div className="card-pad text-noc-muted">Carregando…</div>
      ) : devices.length === 0 ? (
        <div className="card-pad text-noc-muted">
          {blocked ? "Nenhum dispositivo bloqueado." : "Nenhum dispositivo aguardando autorização."}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint bg-noc-panel2/40">
                <tr>
                  <Th>Device ID</Th>
                  <Th>Cliente</Th>
                  <Th>FW</Th>
                  <Th>Visto em</Th>
                  <Th className="text-right">Ações</Th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-t border-noc-border hover:bg-noc-panel2/30">
                    <td className="px-3 py-2 font-mono text-xs text-noc-text">
                      <span title={d.device_id}>{shortDeviceId(d.device_id)}</span>
                      <span
                        className={`ml-2 inline-flex items-center text-[9px] font-bold px-1 py-0.5 rounded border align-middle ${
                          blocked
                            ? "border-noc-err/30 text-noc-err bg-noc-err/10"
                            : "border-noc-warn/30 text-noc-warn bg-noc-warn/10"
                        }`}
                      >
                        {blocked ? "BLOQUEADO" : "PENDENTE"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {d.customer_code || <span className="text-noc-muted">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {d.fw_version || <span className="text-noc-muted">—</span>}
                    </td>
                    <td className="px-3 py-2 text-noc-muted text-xs">{fmtWhen(d.last_seen)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onAuthorize(d)}
                          disabled={busyId === d.id}
                          className="text-xs px-2.5 py-1.5 rounded-lg border border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {busyId === d.id ? "…" : "Autorizar"}
                        </button>
                        {!blocked && (
                          <button
                            type="button"
                            onClick={() => onBlock(d)}
                            disabled={busyId === d.id}
                            className="text-xs px-2.5 py-1.5 rounded-lg border border-noc-err/30 text-noc-err hover:bg-noc-err/10 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {busyId === d.id ? "…" : "Bloquear"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, tone, children }) {
  const toneCls =
    tone === "red"
      ? active
        ? "border-noc-err/30 text-noc-err"
        : "border-transparent text-noc-muted hover:text-noc-text"
      : active
        ? "border-noc-warn/30 text-noc-warn"
        : "border-transparent text-noc-muted hover:text-noc-text";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs rounded-md border ${toneCls}`}
    >
      {children}
    </button>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left font-medium px-3 py-2 whitespace-nowrap ${className}`}>
      {children}
    </th>
  );
}
